import React, {
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import {
    SeatBarGraphSlidingBar,
    SeatBarGraphContainer,
    SeatBarGraphDivider,
    SeatCandidateContainer,
    SeatCandidateDetails,
    SeatCandidateImage,
    SeatCandidateNameText,
    SeatCandidatePartyText,
    SeatCandidatesContainer,
    SeatDetailsContainer,
    SeatDetailsText,
    SeatHeaderContainer,
    SeatResultsContainer,
    SeatsStatusText,
    SeatStatusContainer,
    SeatVoteCountContainer,
    SeatVoteCountText,
    SeatVotesContainer,
    LoadingPlaceholder,
    SeatContainerEmbed,
    SeatContainerLink,
    SeatCandidateImageContainer,
    SeatCandidateTppText,
    SeatBarGraphSlidingBars,
} from './SeatCard.styled'
import {
    SeatAllCandidates,
    SeatData,
    SeatTwoCandidatePreferred,
} from '../data/election-data'
import { AllEvents, DataLayerEventName } from '@news-mono/web-common'
import { getInitials, handleImageEvent } from '../components/helpers'
import {
    getCandidateTextState,
    getSeatCandidates,
} from '../FullElectorateCard/FullElectorateCard'
import formatPercentage from '../helpers/GetFormattedPercentage'
interface SeatCardProps {
    seat?: SeatData
    isLoading?: boolean
    onEvent?: (event: AllEvents) => void
    electoratePageUrl?: string
    isEmbed?: boolean
}

export const SeatCard: React.FC<SeatCardProps> = ({
    seat,
    isLoading = false,
    onEvent,
    electoratePageUrl,
    isEmbed = false,
}) => {
    const electorateUrl = electoratePageUrl
        ? `${electoratePageUrl}?search=${seat?.seatName.toLowerCase()}&display=expanded`
        : ''
    const handleSeatClick = useCallback(() => {
        onEvent?.({
            type: DataLayerEventName.navClicked,
            originator: 'ElectionTheSeatsWidget',
            payload: {
                navName: 'ElectionSeats',
                navLocation: 'OnPage',
                navText: seat?.seatName,
                navLink: electorateUrl,
            },
        })
    }, [onEvent, electorateUrl, seat])

    if (isLoading || !seat) {
        return <LoadingPlaceholder />
    }

    const {
        seatName,
        state,
        candidates,
        winningParty,
        status,
        winningPartyDarkColor,
        allCandidates,
    } = seat

    // If the seat is an embed, we don't want to link to the electorate page, so render a div instead of a link
    const SeatContainer = isEmbed ? SeatContainerNoLink : SeatContainerWithLink

    return (
        <SeatContainer
            onClick={handleSeatClick}
            electoratePageUrl={electorateUrl}
        >
            <SeatHeaderContainer>
                <SeatDetailsContainer>
                    <span>
                        <SeatDetailsText variant="name">
                            {seatName}
                        </SeatDetailsText>
                        <SeatDetailsText variant="state">
                            {state}
                        </SeatDetailsText>
                    </span>
                    <SeatCandidateTppText variant="mobile">
                        {getCandidateTextState(candidates, allCandidates)}
                    </SeatCandidateTppText>
                </SeatDetailsContainer>
                {status !== 'Not Called' &&
                    winningParty &&
                    winningPartyDarkColor && (
                        <SeatStatusContainer color={winningPartyDarkColor}>
                            <SeatsStatusText>
                                {winningParty} {status}
                            </SeatsStatusText>
                        </SeatStatusContainer>
                    )}
            </SeatHeaderContainer>
            <SeatResults
                candidates={candidates}
                allCandidates={allCandidates}
            />
        </SeatContainer>
    )
}

type SeatContainerProps = {
    onClick?: () => void
    electoratePageUrl?: string
}

const SeatContainerWithLink = ({
    children,
    onClick,
    electoratePageUrl,
}: PropsWithChildren<SeatContainerProps>) => (
    <SeatContainerLink to={electoratePageUrl!} onClick={onClick}>
        {children}
    </SeatContainerLink>
)
const SeatContainerNoLink = ({
    children,
}: PropsWithChildren<SeatContainerProps>) => (
    <SeatContainerEmbed>{children}</SeatContainerEmbed>
)

const SeatResults: React.FC<{
    candidates: SeatTwoCandidatePreferred[]
    allCandidates: SeatAllCandidates[]
}> = ({ candidates, allCandidates }) => {
    const [candidateOne, candidateTwo] = getSeatCandidates(
        candidates,
        allCandidates,
    )

    // If there are no candidates we want to display nothing in the cards
    const isEmptyCandidateOne = !candidateOne.candidateName && !candidateOne.partyName && !candidateOne.incumbent
    const isEmptyCandidateTwo = !candidateTwo.candidateName && !candidateTwo.partyName && !candidateTwo.incumbent
    
    const isTppState = candidates.length !== 0

    const { candidateOneBarPercentage, candidateTwoBarPercentage } = getBarPercentage(
        candidateOne.primaryVotes,
        candidateTwo.primaryVotes,
    )

    return (
        <SeatResultsContainer>
            <SeatVotesContainer>
                {!isEmptyCandidateOne && <VoteDisplay
                    votes={candidateOne.primaryVotes}
                    percentage={candidateOne.currentVotesPercentage}
                    color={candidateOne.partyColor.dark}
                    reverse={true}
                    showPercentage={isTppState}
                />}
                <SeatCandidateTppText variant="desktop">
                    {getCandidateTextState(candidates, allCandidates)}
                </SeatCandidateTppText>
                {!isEmptyCandidateTwo && <VoteDisplay
                    votes={candidateTwo.primaryVotes}
                    percentage={candidateTwo.currentVotesPercentage}
                    color={candidateTwo.partyColor.dark}
                    reverse={false}
                    showPercentage={isTppState}
                />}
            </SeatVotesContainer>
            <SeatBarGraph
                candidateOneColor={candidateOne.partyColor.primary}
                candidateTwoColor={candidateTwo.partyColor.primary}
                candidateOnePct={isTppState ? candidateOne.currentVotesPercentage : candidateOneBarPercentage}
                candidateTwoPct={candidateTwoBarPercentage}
                isTppState={isTppState}
            ></SeatBarGraph>
            <SeatCandidatesContainer>
                {!isEmptyCandidateOne && <CandidateDisplay
                    candidateName={candidateOne.candidateName}
                    color={candidateOne.partyColor.dark}
                    partyName={
                        candidateOne.shortPartyName || candidateOne.partyName
                    }
                    incumbent={candidateOne.incumbent}
                    imageUrl={candidateOne.imageUrl}
                    reverse={false}
                    isIncumbentParty={candidateOne.isIncumbentParty}
                    awaitingResults={candidateOne.awaitingResults}
                />}
                {!isEmptyCandidateTwo && <CandidateDisplay
                    candidateName={candidateTwo.candidateName}
                    color={candidateTwo.partyColor.dark}
                    partyName={
                        candidateTwo.shortPartyName || candidateOne.partyName
                    }
                    incumbent={candidateTwo.incumbent} // NB this should always be false, or something has gone very wrong with democracy
                    imageUrl={candidateTwo.imageUrl}
                    reverse={true}
                    isIncumbentParty={candidateTwo.isIncumbentParty}
                    awaitingResults={candidateOne.awaitingResults}
                />}
            </SeatCandidatesContainer>
        </SeatResultsContainer>
    )
}

const VoteDisplay: React.FC<{
    votes: number
    percentage: number | null
    color: string
    reverse?: boolean
    showPercentage?: boolean
}> = ({ votes, percentage, color, reverse = false, showPercentage = true }) => {
    const formattedPercentage = formatPercentage(percentage)
    return (
        <SeatVoteCountContainer color={color}>
            {reverse ? (
                <>
                    {showPercentage && (
                        <SeatVoteCountText variant="percentage">
                            {formattedPercentage && `${formattedPercentage}%`}
                        </SeatVoteCountText>
                    )}
                    <SeatVoteCountText variant="count">
                        {votes.toLocaleString()}
                    </SeatVoteCountText>
                </>
            ) : (
                <>
                    <SeatVoteCountText variant="count">
                        {votes.toLocaleString()}
                    </SeatVoteCountText>
                    {showPercentage && (
                        <SeatVoteCountText variant="percentage">
                            {formattedPercentage && `${formattedPercentage}%`}
                        </SeatVoteCountText>
                    )}
                </>
            )}
        </SeatVoteCountContainer>
    )
}

const CandidateDisplay: React.FC<{
    candidateName: string
    color: string
    partyName: string | null
    incumbent: boolean
    imageUrl: string
    reverse?: boolean
    isIncumbentParty: boolean
    awaitingResults?: boolean
}> = ({
    candidateName,
    color,
    partyName,
    incumbent,
    imageUrl,
    reverse = false,
    isIncumbentParty,
    awaitingResults,
}) => {
    const imgRef = useRef<HTMLImageElement>(null)
    const [imgSrc, setImgSrc] = useState(imageUrl)

    useEffect(() => {
        setImgSrc(`${imageUrl}&${new Date().getTime()}`)
    }, [imageUrl])

    const displayName = awaitingResults ? '' : candidateName

    return (
        <SeatCandidateContainer>
            {reverse ? (
                <>
                    <SeatCandidateDetails reverse={reverse}>
                        <SeatCandidatePartyText color={color}>
                            {partyName}
                            {incumbent ? (
                                <span>&nbsp;(Incumbent)</span>
                            ) : isIncumbentParty ? (
                                <span>&nbsp;(Incumbent Party)</span>
                            ) : null}
                        </SeatCandidatePartyText>
                        <SeatCandidateNameText>
                            {displayName}
                        </SeatCandidateNameText>
                    </SeatCandidateDetails>
                    <SeatCandidateImageContainer
                        initials={getInitials(candidateName)}
                    >
                        <SeatCandidateImage
                            ref={imgRef}
                            src={imgSrc}
                            alt={candidateName}
                            onError={(e) => handleImageEvent(e)}
                        />
                    </SeatCandidateImageContainer>
                </>
            ) : (
                <>
                    <SeatCandidateImageContainer
                        initials={getInitials(candidateName)}
                    >
                        <SeatCandidateImage
                            ref={imgRef}
                            src={imgSrc}
                            alt={candidateName}
                            onError={(e) => handleImageEvent(e)}
                        />
                    </SeatCandidateImageContainer>
                    <SeatCandidateDetails reverse={reverse}>
                        <SeatCandidatePartyText color={color}>
                            {partyName}
                            {incumbent ? (
                                <span>&nbsp;(Incumbent)</span>
                            ) : isIncumbentParty ? (
                                <span>&nbsp;(Incumbent Party)</span>
                            ) : null}
                        </SeatCandidatePartyText>
                        <SeatCandidateNameText>
                            {displayName}
                        </SeatCandidateNameText>
                    </SeatCandidateDetails>
                </>
            )}
        </SeatCandidateContainer>
    )
}

export const SeatBarGraph: React.FC<{
    candidateOneColor: string
    candidateTwoColor: string
    candidateOnePct: number | null
    candidateTwoPct: number | null
    isTppState?: boolean
}> = ({ candidateOneColor, candidateTwoColor, candidateOnePct, candidateTwoPct, isTppState = false }) => {

    if(isTppState){ 
        return (
            <SeatBarGraphContainer>
                <SeatBarGraphSlidingBars>
                    {candidateOnePct !== null && (
                        <>
                            <SeatBarGraphSlidingBar
                                color={candidateOneColor}
                                width={candidateOnePct}
                            />
                            <SeatBarGraphSlidingBar
                                color={candidateTwoColor}
                                width={100 - candidateOnePct}
                            />
                        </>
                    )}
                </SeatBarGraphSlidingBars>
                <SeatBarGraphDivider />
            </SeatBarGraphContainer>
        )
    }

    return (
        <SeatBarGraphContainer>
            <SeatBarGraphSlidingBars>
                {candidateOnePct !== null && candidateTwoPct !== null && (
                    <>
                        <SeatBarGraphSlidingBar
                            color={candidateOneColor}
                            width={candidateOnePct}
                        />
                        <SeatBarGraphSlidingBar
                            color={candidateTwoColor}
                            width={candidateTwoPct}
                            isRight={true}
                        />
                    </>
                )}
            </SeatBarGraphSlidingBars>
            <SeatBarGraphDivider />
        </SeatBarGraphContainer>
    )
}

export const getBarPercentage = (
    candidateOneVotes: number,
    candidateTwoVotes: number,
) => {
    const isCandidateOneWinning = candidateOneVotes > candidateTwoVotes
    const barTotal = Math.max(candidateOneVotes, candidateTwoVotes)
    const candidateOneBarPercentage = isCandidateOneWinning ? 50 : Math.round(candidateOneVotes * 50 / barTotal)
    const candidateTwoBarPercentage = isCandidateOneWinning ? Math.round(candidateTwoVotes * 50 / barTotal) : 50

    return {
        candidateOneBarPercentage,
        candidateTwoBarPercentage,
    }
}
